import { EventEmitter } from 'events';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../../utils/logger';
import { ConfigManager } from '../config-manager';
import { IStorage, FileStorage } from '../storage';

// 工作流状态
export enum WorkflowStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

// 步骤状态
export enum StepStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SKIPPED = 'skipped',
  PAUSED = 'paused'
}

// 步骤类型
export enum StepType {
  MANUAL = 'manual',
  AUTOMATIC = 'automatic',
  CONDITIONAL = 'conditional',
  PARALLEL = 'parallel',
  LOOP = 'loop'
}

// 工作流步骤
export interface WorkflowStep {
  id: string;
  name: string;
  description?: string;
  type: StepType;
  status: StepStatus;
  dependencies: string[];
  actions: WorkflowAction[];
  conditions?: WorkflowCondition[];
  parallel?: boolean;
  retryable?: boolean;
  maxRetries?: number;
  timeout?: number;
  metadata?: Record<string, any>;
}

// 工作流动作
export interface WorkflowAction {
  type: 'command' | 'script' | 'function' | 'prompt';
  name: string;
  params?: Record<string, any>;
  handler?: (context: WorkflowContext) => Promise<any>;
}

// 工作流条件
export interface WorkflowCondition {
  type: 'expression' | 'function';
  expression?: string;
  handler?: (context: WorkflowContext) => Promise<boolean>;
  onTrue?: string; // 下一步ID
  onFalse?: string; // 下一步ID
}

// 工作流上下文
export interface WorkflowContext {
  workflowId: string;
  variables: Record<string, any>;
  results: Record<string, any>;
  currentStep?: string;
  history: WorkflowEvent[];
}

// 工作流事件
export interface WorkflowEvent {
  id: string;
  type: 'started' | 'step_completed' | 'step_failed' | 'paused' | 'resumed' | 'completed' | 'failed';
  stepId?: string;
  timestamp: number;
  data?: any;
  error?: string;
}

// 工作流定义
export interface WorkflowDefinition {
  id: string;
  name: string;
  description?: string;
  version: string;
  steps: WorkflowStep[];
  variables?: Record<string, any>;
  triggers?: WorkflowTrigger[];
  timeout?: number;
  metadata?: Record<string, any>;
}

// 工作流触发器
export interface WorkflowTrigger {
  type: 'manual' | 'schedule' | 'event' | 'webhook';
  config: Record<string, any>;
}

// 工作流实例
export interface WorkflowInstance {
  id: string;
  definitionId: string;
  status: WorkflowStatus;
  context: WorkflowContext;
  startedAt: number;
  updatedAt: number;
  completedAt?: number;
  error?: string;
  currentStep?: string;
  pausedAt?: number;
  resumedAt?: number;
}

// 工作流执行选项
export interface WorkflowExecutionOptions {
  variables?: Record<string, any>;
  startFrom?: string; // 从特定步骤开始
  skipSteps?: string[]; // 跳过的步骤
  parallel?: boolean; // 启用并行执行
  dryRun?: boolean; // 模拟运行
}

// 工作流执行引擎
export class WorkflowEngine extends EventEmitter {
  private definitions = new Map<string, WorkflowDefinition>();
  private instances = new Map<string, WorkflowInstance>();
  private storage: IStorage;
  private executors = new Map<string, NodeJS.Timeout>();
  
  constructor(
    private configManager: ConfigManager,
    storagePath?: string
  ) {
    super();
    this.storage = new FileStorage(
      storagePath || '.workflow/engine'
    );
    this.loadState();
  }
  
  /**
   * 注册工作流定义
   */
  registerDefinition(definition: WorkflowDefinition): void {
    this.validateDefinition(definition);
    this.definitions.set(definition.id, definition);
    logger.info(`Registered workflow definition: ${definition.name} (${definition.id})`);
    this.emit('definitionRegistered', definition);
  }
  
  /**
   * 启动工作流
   */
  async start(
    definitionId: string,
    options: WorkflowExecutionOptions = {}
  ): Promise<WorkflowInstance> {
    const definition = this.definitions.get(definitionId);
    if (!definition) {
      throw new Error(`Workflow definition not found: ${definitionId}`);
    }
    
    // 创建工作流实例
    const instance: WorkflowInstance = {
      id: uuidv4(),
      definitionId,
      status: WorkflowStatus.PENDING,
      context: {
        workflowId: definitionId,
        variables: { ...definition.variables, ...options.variables },
        results: {},
        history: []
      },
      startedAt: Date.now(),
      updatedAt: Date.now()
    };
    
    // 初始化步骤状态
    const steps = this.prepareSteps(definition.steps, options);
    
    // 保存实例
    this.instances.set(instance.id, instance);
    await this.saveInstance(instance);
    
    // 记录启动事件
    await this.recordEvent(instance, {
      type: 'started',
      data: { options }
    });
    
    // 开始执行
    if (!options.dryRun) {
      this.executeWorkflow(instance, definition, steps);
    }
    
    logger.info(`Started workflow: ${definition.name} (${instance.id})`);
    this.emit('workflowStarted', instance);
    
    return instance;
  }
  
  /**
   * 暂停工作流
   */
  async pause(instanceId: string): Promise<void> {
    const instance = this.instances.get(instanceId);
    if (!instance) {
      throw new Error(`Workflow instance not found: ${instanceId}`);
    }
    
    if (instance.status !== WorkflowStatus.RUNNING) {
      throw new Error(`Cannot pause workflow in status: ${instance.status}`);
    }
    
    // 停止执行器
    const executor = this.executors.get(instanceId);
    if (executor) {
      clearTimeout(executor);
      this.executors.delete(instanceId);
    }
    
    // 更新状态
    instance.status = WorkflowStatus.PAUSED;
    instance.pausedAt = Date.now();
    instance.updatedAt = Date.now();
    
    await this.saveInstance(instance);
    await this.recordEvent(instance, { type: 'paused' });
    
    logger.info(`Paused workflow: ${instanceId}`);
    this.emit('workflowPaused', instance);
  }
  
  /**
   * 恢复工作流
   */
  async resume(instanceId: string): Promise<void> {
    const instance = this.instances.get(instanceId);
    if (!instance) {
      throw new Error(`Workflow instance not found: ${instanceId}`);
    }
    
    if (instance.status !== WorkflowStatus.PAUSED) {
      throw new Error(`Cannot resume workflow in status: ${instance.status}`);
    }
    
    const definition = this.definitions.get(instance.definitionId);
    if (!definition) {
      throw new Error(`Workflow definition not found: ${instance.definitionId}`);
    }
    
    // 更新状态
    instance.status = WorkflowStatus.RUNNING;
    instance.resumedAt = Date.now();
    instance.updatedAt = Date.now();
    
    await this.saveInstance(instance);
    await this.recordEvent(instance, { type: 'resumed' });
    
    // 继续执行
    const steps = this.prepareSteps(definition.steps);
    this.executeWorkflow(instance, definition, steps);
    
    logger.info(`Resumed workflow: ${instanceId}`);
    this.emit('workflowResumed', instance);
  }
  
  /**
   * 取消工作流
   */
  async cancel(instanceId: string, reason?: string): Promise<void> {
    const instance = this.instances.get(instanceId);
    if (!instance) {
      throw new Error(`Workflow instance not found: ${instanceId}`);
    }
    
    if ([WorkflowStatus.COMPLETED, WorkflowStatus.FAILED, WorkflowStatus.CANCELLED].includes(instance.status)) {
      throw new Error(`Cannot cancel workflow in status: ${instance.status}`);
    }
    
    // 停止执行器
    const executor = this.executors.get(instanceId);
    if (executor) {
      clearTimeout(executor);
      this.executors.delete(instanceId);
    }
    
    // 更新状态
    instance.status = WorkflowStatus.CANCELLED;
    instance.error = reason || 'Cancelled by user';
    instance.completedAt = Date.now();
    instance.updatedAt = Date.now();
    
    await this.saveInstance(instance);
    await this.recordEvent(instance, {
      type: 'failed',
      error: instance.error
    });
    
    logger.info(`Cancelled workflow: ${instanceId}`);
    this.emit('workflowCancelled', instance);
  }
  
  /**
   * 获取工作流实例
   */
  getInstance(instanceId: string): WorkflowInstance | undefined {
    return this.instances.get(instanceId);
  }
  
  /**
   * 获取所有实例
   */
  getInstances(filter?: {
    status?: WorkflowStatus;
    definitionId?: string;
    limit?: number;
  }): WorkflowInstance[] {
    let instances = Array.from(this.instances.values());
    
    if (filter) {
      if (filter.status) {
        instances = instances.filter(i => i.status === filter.status);
      }
      if (filter.definitionId) {
        instances = instances.filter(i => i.definitionId === filter.definitionId);
      }
      if (filter.limit) {
        instances = instances.slice(0, filter.limit);
      }
    }
    
    return instances.sort((a, b) => b.startedAt - a.startedAt);
  }
  
  /**
   * 获取工作流历史
   */
  getHistory(instanceId: string): WorkflowEvent[] {
    const instance = this.instances.get(instanceId);
    return instance?.context.history || [];
  }
  
  /**
   * 执行工作流
   */
  private async executeWorkflow(
    instance: WorkflowInstance,
    definition: WorkflowDefinition,
    steps: Map<string, WorkflowStep>
  ): Promise<void> {
    instance.status = WorkflowStatus.RUNNING;
    instance.updatedAt = Date.now();
    await this.saveInstance(instance);
    
    try {
      // 找到可执行的步骤
      const executableSteps = this.findExecutableSteps(steps, instance);
      
      if (executableSteps.length === 0) {
        // 检查是否所有步骤都完成
        const allCompleted = Array.from(steps.values()).every(
          step => step.status === StepStatus.COMPLETED || step.status === StepStatus.SKIPPED
        );
        
        if (allCompleted) {
          await this.completeWorkflow(instance);
        } else {
          // 没有可执行步骤但也没完成，可能有错误
          await this.failWorkflow(instance, 'No executable steps found');
        }
        return;
      }
      
      // 并行执行步骤
      const promises = executableSteps.map(step => 
        this.executeStep(instance, definition, step, steps)
      );
      
      await Promise.all(promises);
      
      // 继续执行下一批步骤
      const timeout = setTimeout(() => {
        this.executors.delete(instance.id);
        this.executeWorkflow(instance, definition, steps);
      }, 100);
      
      this.executors.set(instance.id, timeout);
      
    } catch (error) {
      await this.failWorkflow(instance, error.message);
    }
  }
  
  /**
   * 执行步骤
   */
  private async executeStep(
    instance: WorkflowInstance,
    definition: WorkflowDefinition,
    step: WorkflowStep,
    allSteps: Map<string, WorkflowStep>
  ): Promise<void> {
    logger.debug(`Executing step: ${step.name} (${step.id})`);
    
    step.status = StepStatus.RUNNING;
    instance.currentStep = step.id;
    instance.updatedAt = Date.now();
    await this.saveInstance(instance);
    
    try {
      // 检查条件
      if (step.conditions && step.conditions.length > 0) {
        const conditionMet = await this.evaluateConditions(step.conditions, instance.context);
        if (!conditionMet) {
          step.status = StepStatus.SKIPPED;
          logger.info(`Step skipped due to conditions: ${step.name}`);
          return;
        }
      }
      
      // 执行动作
      const results: any[] = [];
      for (const action of step.actions) {
        const result = await this.executeAction(action, instance.context);
        results.push(result);
      }
      
      // 保存结果
      instance.context.results[step.id] = results;
      step.status = StepStatus.COMPLETED;
      
      await this.recordEvent(instance, {
        type: 'step_completed',
        stepId: step.id,
        data: { results }
      });
      
      logger.info(`Step completed: ${step.name}`);
      this.emit('stepCompleted', { instance, step });
      
    } catch (error) {
      logger.error(`Step failed: ${step.name}`, error);
      
      // 重试逻辑
      if (step.retryable && step.metadata?.retryCount < (step.maxRetries || 3)) {
        step.metadata = { ...step.metadata, retryCount: (step.metadata?.retryCount || 0) + 1 };
        logger.info(`Retrying step: ${step.name} (attempt ${step.metadata.retryCount})`);
        
        setTimeout(() => {
          this.executeStep(instance, definition, step, allSteps);
        }, 1000 * step.metadata.retryCount);
        
        return;
      }
      
      step.status = StepStatus.FAILED;
      await this.recordEvent(instance, {
        type: 'step_failed',
        stepId: step.id,
        error: error.message
      });
      
      this.emit('stepFailed', { instance, step, error });
      
      // 如果步骤失败且不可重试，工作流失败
      if (!step.retryable || step.metadata?.retryCount >= (step.maxRetries || 3)) {
        await this.failWorkflow(instance, `Step failed: ${step.name} - ${error.message}`);
      }
    } finally {
      instance.updatedAt = Date.now();
      await this.saveInstance(instance);
    }
  }
  
  /**
   * 执行动作
   */
  private async executeAction(
    action: WorkflowAction,
    context: WorkflowContext
  ): Promise<any> {
    switch (action.type) {
      case 'command':
        // 执行命令（需要实现命令执行器）
        logger.debug(`Executing command: ${action.name}`);
        return { command: action.name, params: action.params };
        
      case 'script':
        // 执行脚本（需要实现脚本执行器）
        logger.debug(`Executing script: ${action.name}`);
        return { script: action.name, params: action.params };
        
      case 'function':
        // 执行函数
        if (action.handler) {
          return action.handler(context);
        }
        throw new Error(`No handler for function action: ${action.name}`);
        
      case 'prompt':
        // 提示用户输入（需要实现交互式输入）
        logger.debug(`Prompting user: ${action.name}`);
        return { prompt: action.name, params: action.params };
        
      default:
        throw new Error(`Unknown action type: ${action.type}`);
    }
  }
  
  /**
   * 评估条件
   */
  private async evaluateConditions(
    conditions: WorkflowCondition[],
    context: WorkflowContext
  ): Promise<boolean> {
    for (const condition of conditions) {
      let result: boolean;
      
      if (condition.type === 'expression') {
        // 简单的表达式评估（实际应该使用安全的表达式引擎）
        result = this.evaluateExpression(condition.expression!, context);
      } else if (condition.handler) {
        result = await condition.handler(context);
      } else {
        throw new Error('Invalid condition: no expression or handler');
      }
      
      if (!result) {
        return false;
      }
    }
    
    return true;
  }
  
  /**
   * 评估表达式
   */
  private evaluateExpression(expression: string, context: WorkflowContext): boolean {
    // 这是一个简化的实现，实际应该使用安全的表达式引擎
    try {
      // 替换变量
      let evaluatedExpression = expression;
      for (const [key, value] of Object.entries(context.variables)) {
        evaluatedExpression = evaluatedExpression.replace(
          new RegExp(`\\$${key}`, 'g'),
          JSON.stringify(value)
        );
      }
      
      // 简单的比较
      if (evaluatedExpression.includes('==')) {
        const [left, right] = evaluatedExpression.split('==').map(s => s.trim());
        return left === right;
      }
      
      return false;
    } catch {
      return false;
    }
  }
  
  /**
   * 查找可执行步骤
   */
  private findExecutableSteps(
    steps: Map<string, WorkflowStep>,
    instance: WorkflowInstance
  ): WorkflowStep[] {
    const executable: WorkflowStep[] = [];
    
    for (const step of steps.values()) {
      // 跳过已完成或正在运行的步骤
      if ([StepStatus.COMPLETED, StepStatus.RUNNING, StepStatus.FAILED, StepStatus.SKIPPED].includes(step.status)) {
        continue;
      }
      
      // 检查依赖
      const dependenciesMet = step.dependencies.every(depId => {
        const dep = steps.get(depId);
        return dep && (dep.status === StepStatus.COMPLETED || dep.status === StepStatus.SKIPPED);
      });
      
      if (dependenciesMet) {
        executable.push(step);
      }
    }
    
    // 如果有并行步骤，一起返回
    return executable.filter(step => {
      // 如果是并行步骤，确保其他并行步骤也准备好
      if (step.parallel) {
        return executable.some(other => other.parallel && other.id !== step.id);
      }
      return true;
    });
  }
  
  /**
   * 完成工作流
   */
  private async completeWorkflow(instance: WorkflowInstance): Promise<void> {
    instance.status = WorkflowStatus.COMPLETED;
    instance.completedAt = Date.now();
    instance.updatedAt = Date.now();
    
    await this.saveInstance(instance);
    await this.recordEvent(instance, { type: 'completed' });
    
    logger.info(`Workflow completed: ${instance.id}`);
    this.emit('workflowCompleted', instance);
  }
  
  /**
   * 工作流失败
   */
  private async failWorkflow(instance: WorkflowInstance, error: string): Promise<void> {
    instance.status = WorkflowStatus.FAILED;
    instance.error = error;
    instance.completedAt = Date.now();
    instance.updatedAt = Date.now();
    
    await this.saveInstance(instance);
    await this.recordEvent(instance, {
      type: 'failed',
      error
    });
    
    logger.error(`Workflow failed: ${instance.id} - ${error}`);
    this.emit('workflowFailed', instance);
  }
  
  /**
   * 记录事件
   */
  private async recordEvent(
    instance: WorkflowInstance,
    event: Omit<WorkflowEvent, 'id' | 'timestamp'>
  ): Promise<void> {
    const fullEvent: WorkflowEvent = {
      ...event,
      id: uuidv4(),
      timestamp: Date.now()
    };
    
    instance.context.history.push(fullEvent);
    await this.saveInstance(instance);
  }
  
  /**
   * 准备步骤
   */
  private prepareSteps(
    steps: WorkflowStep[],
    options?: WorkflowExecutionOptions
  ): Map<string, WorkflowStep> {
    const stepMap = new Map<string, WorkflowStep>();
    
    for (const step of steps) {
      const preparedStep = { ...step };
      
      // 初始化状态
      if (!preparedStep.status) {
        preparedStep.status = StepStatus.PENDING;
      }
      
      // 处理跳过的步骤
      if (options?.skipSteps?.includes(step.id)) {
        preparedStep.status = StepStatus.SKIPPED;
      }
      
      // 初始化元数据
      if (!preparedStep.metadata) {
        preparedStep.metadata = {};
      }
      
      stepMap.set(preparedStep.id, preparedStep);
    }
    
    // 处理从特定步骤开始
    if (options?.startFrom) {
      let found = false;
      for (const [id, step] of stepMap) {
        if (!found && id !== options.startFrom) {
          step.status = StepStatus.SKIPPED;
        } else if (id === options.startFrom) {
          found = true;
        }
      }
    }
    
    return stepMap;
  }
  
  /**
   * 验证工作流定义
   */
  private validateDefinition(definition: WorkflowDefinition): void {
    if (!definition.id || !definition.name || !definition.version) {
      throw new Error('Invalid workflow definition: missing required fields');
    }
    
    if (!definition.steps || definition.steps.length === 0) {
      throw new Error('Invalid workflow definition: no steps defined');
    }
    
    // 验证步骤ID唯一性
    const stepIds = new Set<string>();
    for (const step of definition.steps) {
      if (stepIds.has(step.id)) {
        throw new Error(`Duplicate step ID: ${step.id}`);
      }
      stepIds.add(step.id);
      
      // 验证依赖存在
      for (const dep of step.dependencies) {
        if (!definition.steps.some(s => s.id === dep)) {
          throw new Error(`Step "${step.id}" depends on non-existent step "${dep}"`);
        }
      }
    }
    
    // 检测循环依赖
    this.detectCyclicDependencies(definition.steps);
  }
  
  /**
   * 检测循环依赖
   */
  private detectCyclicDependencies(steps: WorkflowStep[]): void {
    const visited = new Set<string>();
    const recursionStack = new Set<string>();
    
    const hasCycle = (stepId: string): boolean => {
      visited.add(stepId);
      recursionStack.add(stepId);
      
      const step = steps.find(s => s.id === stepId);
      if (!step) return false;
      
      for (const dep of step.dependencies) {
        if (!visited.has(dep)) {
          if (hasCycle(dep)) return true;
        } else if (recursionStack.has(dep)) {
          return true;
        }
      }
      
      recursionStack.delete(stepId);
      return false;
    };
    
    for (const step of steps) {
      if (!visited.has(step.id)) {
        if (hasCycle(step.id)) {
          throw new Error('Cyclic dependency detected in workflow steps');
        }
      }
    }
  }
  
  /**
   * 保存实例
   */
  private async saveInstance(instance: WorkflowInstance): Promise<void> {
    await this.storage.set(`instance:${instance.id}`, instance);
  }
  
  /**
   * 加载状态
   */
  private async loadState(): Promise<void> {
    try {
      const keys = await this.storage.list('instance:*');
      
      for (const key of keys) {
        const instance = await this.storage.get(key);
        if (instance) {
          this.instances.set(instance.id, instance);
        }
      }
      
      logger.info(`Loaded ${this.instances.size} workflow instances`);
    } catch (error) {
      logger.warn('Failed to load workflow state:', error);
    }
  }
  
  /**
   * 清理已完成的实例
   */
  async cleanup(olderThan: number = 7 * 24 * 60 * 60 * 1000): Promise<number> {
    const cutoff = Date.now() - olderThan;
    let cleaned = 0;
    
    for (const [id, instance] of this.instances) {
      if (instance.completedAt && instance.completedAt < cutoff) {
        this.instances.delete(id);
        await this.storage.delete(`instance:${id}`);
        cleaned++;
      }
    }
    
    logger.info(`Cleaned up ${cleaned} old workflow instances`);
    return cleaned;
  }
}